<template>
    <div>
        <DCard
            padding
            class="form-row-no-input"
        >
            <div>
                <div class="d-inline-flex my-2 action-list">
                    <DDropdown
                        id="actions_dropdown"
                        color="secondary"
                        placement="bottom-end"
                        add-menu-classes="pt-0 pb-0"
                        class="me-1"
                        :toggler-text="__('customers.invoices.download')"
                    >
                        <DDropdownItem
                            id="action_download"
                            :href="getDownloadInvoiceLink()"
                        >
                            <div class="row pl-1 pr-1">
                                <div>
                                    {{ invoice.type.startsWith('invoice')
                                        ? __('customers.invoices.invoice')
                                        : __('customers.credit_notes.credit_note')
                                    }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-for="(dunning, index) in invoice.dunning_letters"
                            :id="`dunning_download_${dunning.name}`"
                            :key="index"
                            :href="getDownloadDunningLink(dunning.id)"
                        >
                            <div class="row pl-1 pr-1">
                                <div>
                                    {{ dunning.name }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="invoice.type.startsWith('invoice') && customer.invoice_csv"
                            id="action_download_csv"
                            :href="getDownloadInvoiceCsvLink(invoice)"
                        >
                            <div class="row pl-1 pr-1">
                                <div>
                                    {{ __('customers.invoices.invoice_csv') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="invoice.type.startsWith('invoice') && invoice.hasPartnerSubContractsCsv"
                            id="action_download_partner_csv"
                            :href="getDownloadInvoicePartnersSubContractsCsvLink(invoice)"
                        >
                            <div class="row pl-1 pr-1">
                                <div>
                                    {{ __('customers.invoices.invoice_partner_subcontracts_csv') }}
                                </div>
                            </div>
                        </DDropdownItem>
                    </DDropdown>
                    <DButton
                        v-if="cancelAvailable"
                        id="action_cancel"
                        color="secondary"
                        :label="__('customers.invoices.cancel')"
                        @click="showCancelModal = true"
                    />
                    <DButton
                        v-if="cancelAvailable"
                        id="action_cancel_partially"
                        color="secondary"
                        :label="__('customers.invoices.cancel_partially')"
                        @click="cancelPartially()"
                    />
                    <DButton
                        v-if="invoice.type.startsWith('invoice') && userPermissions.includes('invoice.resend_document')"
                        id="action_resend"
                        color="secondary"
                        :href="resendInvoiceEmail(invoice)"
                        :label="__(('tools.invoices.resend_invoice.' + invoice.customer_delivery_type))"
                    />
                    <DButton
                        v-if="invoice.type.startsWith('invoice')
                            && userPermissions.includes('invoice.manage')
                            && invoice.manualPaymentAvailable === true"
                        id="action_trigger_payment"
                        color="secondary"
                        :label="__(('tools.invoices.trigger_payment'))"
                        @click="triggerPayment(invoice)"
                    />
                    <DButton
                        v-if="invoice.type.startsWith('invoice')
                            && userPermissions.includes('invoice.manage')
                            && invoice.status !== 'paid'
                            && hasAlternativePaymentMethods(invoice)"
                        id="change_payment_method"
                        color="secondary"
                        :label="__('customers.invoices.change_payment_method')"
                        @click="showChangePaymentMethodModal = true"
                    />
                    <DButton
                        v-if="invoice.type.startsWith('invoice')
                            && invoice.status === 'unpaid'
                            && userPermissions.includes('invoice.set_debt_loss')
                            && !invoice.debt_loss"
                        id="set_debt_loss"
                        color="secondary"
                        :label="__('tools.invoices.set_debt_loss')"
                        @click="openSetDebtLossModal(item)"
                    />
                    <div class="m-auto ms-3">
                        <IncidentFlag
                            v-if="userPermissions.includes('customer.incidents_view') && invoice.has_incidents"
                            id="invoice-incident-flag"
                            :active-incident="invoice.has_open_incidents"
                            :relation-type="'invoices'"
                            :relation-id="invoice.id"
                            :customer-id="invoice.customer_id"
                        />
                    </div>
                </div>
            </div>
            <DRow>
                <DCol
                    padding
                    lg="6"
                >
                    <DFormRow
                        v-if="invoice.dunning_status !== null || invoice.debt_loss"
                        :label="__('customers.invoices.dunning_status')"
                    >
                        <DTag
                            v-if="invoice.in_lawsuit"
                            color="danger"
                            :label="__('customers.invoices.in_lawsuit')"
                        />
                        <DTag
                            v-else-if="invoice.dunning_status !== null"
                            color="danger"
                            :label="__('customers.invoices.dunning_letters.types.' + invoice.dunning_status)"
                        />
                        <DTag
                            v-if="invoice.debt_loss"
                            color="danger"
                            :label="__('customers.invoices.debt_loss')"
                        />
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.invoice_id')"
                    >
                        {{ invoice.id }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.customer_id')"
                    >
                        {{ invoice.customer_id }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.type')"
                    >
                        {{ __('customers.contacts.invoice_type.' + invoice.type) }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.date')"
                    >
                        {{ setDateFormatEuropeBerlin(invoice.date) }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.customer_data')"
                    >
                        {{ customerData }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.customers.vat_number')"
                    >
                        {{ invoice.vat_number }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.payment_condition')"
                    >
                        <Link
                            v-if="invoice.payment_method === 'credit_card' && userPermissions.includes('payment.credit_card')"
                            :href="ccPaymentsLink()"
                        >
                            {{ invoice.payment_method }}
                        </Link>
                        <span v-else>
                            {{ invoice.payment_method }}
                        </span><strong v-if="invoice.sepa_chargeback === true">({{ __('customers.invoices.chargeback') }})</strong>{{ paymentCondition }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.net_amount')"
                    >
                        {{ roundPrice(invoice.net_amount) }} {{ __('customers.invoices.sum_of_net_prices', {
                            'net_prices': roundPrice(netAmountsSum),
                            'discounts': roundPrice(discountsSum)
                        }) }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.total_amount')"
                    >
                        {{ priceFormat(invoice.total_amount, 2, customer.market_region_id) }}
                    </DFormRow>
                    <div v-if="invoice.used_credit_balance">
                        <DFormRow
                            :label="__('customers.invoices.minus_credit_balance')"
                        >
                            -{{ roundPrice(invoice.used_credit_balance) }}
                        </DFormRow>
                        <DFormRow
                            :label="__('customers.invoices.amount_to_pay')"
                        >
                            {{ roundPrice(invoice.amount_to_pay) }}
                        </DFormRow>
                    </div>
                    <DFormRow
                        :label="__('customers.invoices.open_item_amount')"
                    >
                        {{ roundPrice(invoice.open_item_amount) }}
                    </DFormRow>
                    <DFormRow
                        :label="__('customers.invoices.status')"
                    >
                        {{ formatInvoiceStatus(invoice) }}
                    </DFormRow>
                    <DFormRow
                        v-if="checkPaymentMethodIsSepa(invoice)
                            && invoice.status !== 'unpaid'
                            && !invoiceWithInstallments(invoice)"
                        :label="__('customers.invoices.sepa_transaction')"
                    >
                        <span v-if="invoice.sepa_transaction">
                            {{ invoice.sepa_transaction.id }}
                        </span>
                        <span
                            v-else
                            class="text-danger"
                        >
                            missing
                        </span>
                    </DFormRow>
                    <DFormRow
                        v-if="checkPaymentMethodIsSepa(invoice)
                            && invoice.status !== 'unpaid'
                            && !invoiceWithInstallments(invoice)"
                        :label="__('customers.invoices.sepa_booking_date')"
                    >
                        <span v-if="invoice.sepa_transaction?.booking_date">
                            {{ setDateFormat(invoice.sepa_transaction.booking_date) }}
                        </span>
                    </DFormRow>
                    <!-- Sepa transaction accounting action btn -->
                    <DFormRow
                        v-if="checkPaymentMethodIsSepa(invoice)
                            && invoice.status === 'payment_pending'
                            && (!invoice?.installments || invoice.installments.length === 0)
                            && !invoice.sepa_transaction"
                        label=" "
                    >
                        <DButton
                            v-if="sepaResetAvailableInvoice(invoice)"
                            color="secondary"
                            :label="__('customers.invoices.reset_sepa_payments')"
                            @click="sepaReset(invoice)"
                        />
                    </DFormRow>
                </DCol>
                <DCol
                    v-if="invoice?.installments && invoice.installments.length > 0"
                    padding
                    lg="6"
                    class="instalments-container"
                >
                    <div class="pb-3">
                        <strong>{{ __('customers.invoices.installments.header') }} </strong>
                    </div>

                    <DTable
                        :items="invoice.installments || []"
                        :fields="installments"
                        :header="true"
                    >
                        <template #due_date="{item}">
                            <td>{{ setDateFormatEuropeBerlin(item.due_date) }}</td>
                        </template>
                        <template #open_items="{item}">
                            <td>{{ roundPrice(item.open_items) ?? '' }}</td>
                        </template>
                        <template #amount="{item}">
                            <td> {{ roundPrice(item.amount) }}</td>
                        </template>
                        <template #amount_to_pay="{item}">
                            <td> {{ roundPrice(item.amount_to_pay) }}</td>
                        </template>
                        <template #sepa_transaction="{item}">
                            <td>
                                <span v-if="item.status === 'payment_pending' || item.status === 'paid'">
                                    <span v-if="item.sepa_transaction?.id">{{ item.sepa_transaction.id }}</span>
                                    <span
                                        v-else-if="item.status === 'payment_pending'"
                                        class="text-danger"
                                    >missing</span>
                                </span>
                            </td>
                        </template>
                        <template #sepa_booking_date="{item}">
                            <td> {{ item.sepa_transaction?.booking_date ? setDateFormat(item.sepa_transaction?.booking_date) : '' }}</td>
                        </template>
                        <template #dunning_status="{item}">
                            <td> {{ item.dunning_status !== null ? __('customers.invoices.dunning_letters.types.' + item.dunning_status) : '' }}</td>
                        </template>
                    </DTable>
                    <DButton
                        v-if="editInstallmentsAvailable()"
                        style="float: right"
                        :label="__('customers.invoices.installments.edit_installments_btn')"
                        @click="showInstallmentsEditModal = true"
                    />
                    <!-- Sepa transaction accounting action btn -->
                    <DButton
                        v-if="sepaResetAvailableInstallments(invoice)"
                        color="secondary"
                        :label="__('customers.invoices.reset_sepa_payments')"
                        @click="sepaReset(invoice)"
                    />
                </DCol>
            </DRow>
        </DCard>
        <DCard
            padding
            class="mt-3"
        >
            <DRow
                align-vertical="end"
            >
                <DCol
                    sm="5"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="filterData.search"
                        :label="__('messages.search')"
                        :placeholder="__('customers.invoices.search_placeholder')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        :label="__('messages.actions.filter')"
                        @click="filter(filterData)"
                    />
                    <DButton
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
                <DCol sm="4">
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        class="float-end"
                        @click="showListViewModal = true"
                    />
                </DCol>
            </DRow>
            <DRow class="mt-4">
                <DCol>
                    <DTable
                        v-if="invoice.shouldGroupItems"
                        :items="invoice.groupedItems || []"
                        :fields="groupedItemFields"
                        :header="true"
                    >
                        <template #group_name="{item}">
                            <td>{{ item.group_name }} ({{ item.counted_items }})</td>
                        </template>
                        <template #tax_rate="{item}">
                            <td>{{ taxRate(item.tax_rate) }}</td>
                        </template>
                        <template #from_date="{item}">
                            <td>{{ setDateFormat(item.from_date) }}</td>
                        </template>
                        <template #to_date="{item}">
                            <td>{{ setDateFormat(item.to_date) }}</td>
                        </template>
                        <template #quantity="{item}">
                            <td>{{ showQuantity(item.quantity) }}</td>
                        </template>
                        <template #price="{item}">
                            <td>{{ roundPrice(item.price) }}</td>
                        </template>
                        <template #net_amount="{item}">
                            <td>{{ roundPrice(item.net_amount) }}</td>
                        </template>
                        <template #action="{item}">
                            <td>
                                <DButton
                                    class="pointer-events float-right"
                                    icon="eye"
                                    mini
                                    @click="groupItemsList(filterData, item)"
                                />
                                <DButton
                                    v-if="showUpdateAccountingProductButton(item)"
                                    class="pointer-events float-right"
                                    icon="pencil"
                                    mini
                                    @click="showUpdateAccountingProductModalForItem(item)"
                                />
                            </td>
                        </template>
                    </DTable>
                    <DTable
                        v-else
                        :items="invoice.items || []"
                        :fields="getFields"
                        :sorter="true"
                        :header="true"
                        class="table-responsive"
                    >
                        <template #contract_id="{item}">
                            <td>
                                <Link
                                    v-if="item.contract_id"
                                    :href="$route('customers.customers.contracts.show', {customer: item.customer_id, contract: item.contract_id})"
                                >
                                    {{ item.contract_id }}
                                </Link>
                                <DIcon
                                    v-if="item.customer_id !== '' && item.customer_id !== customer.id && item.contract_id !== ''"
                                    v-c-tooltip="__('customers.invoices.contract_moved_hint')"
                                    name="information-slab-circle-outline"
                                    :size="18"
                                />
                            </td>
                        </template>
                        <template #tax_rate="{item}">
                            <td>{{ taxRate(item.tax_rate) }}</td>
                        </template>
                        <template #order="{item}">
                            <td v-if="item.order_number">
                                <Link
                                    v-if="item.contract_id"
                                    :href="$route('orders.details', {order: item.order_route_id, client: item.order_route_client})"
                                >
                                    {{ item.order_number }}
                                </Link>
                                <template v-else>
                                    {{ item.order_number }}
                                </template>
                            </td>
                            <td v-else />
                        </template>
                        <template #price="{item}">
                            <td>{{ roundPrice(item.price) }}</td>
                        </template>
                        <template #net_amount="{item}">
                            <td>{{ roundPrice(item.net_amount) }}</td>
                        </template>
                        <template #to_date="{item}">
                            <td>{{ setDateFormatEuropeBerlin(item.to_date) }}</td>
                        </template>
                        <template #from_date="{item}">
                            <td>{{ setDateFormatEuropeBerlin(item.from_date) }}</td>
                        </template>
                        <template #invoice_text="{item}">
                            <td>{{ item.invoice_text ?? '' }}</td>
                        </template>
                        <template #tax_amount="{item}">
                            <td>{{ calculateTaxAmount(item) }}</td>
                        </template>
                        <template #external_cost_object_id="{item}">
                            <td>{{ item.external_cost_object_id ?? '' }}</td>
                        </template>
                        <template #external_cost_center_id="{item}">
                            <td>{{ item.external_cost_center_id ?? '' }}</td>
                        </template>
                        <template #revenue_account="{item}">
                            <td>{{ item.revenue_account ?? '' }}</td>
                        </template>
                        <template #expense_account="{item}">
                            <td>{{ item.expense_account ?? '' }}</td>
                        </template>
                        <template #tax_key="{item}">
                            <td>{{ item.tax_key ?? '' }}</td>
                        </template>
                        <template #cancellation_item="{item}">
                            <td>
                                <Link
                                    v-if="item.cancellation_item"
                                    :href="$route('customers.customers.invoices.show', {customer: item.customer_id, invoice: item.cancellation_item})"
                                >
                                    {{ item.cancellation_item + ' - ' + item.cancellation_item_line_item }}
                                </Link>
                                <span v-else>
                                    {{ '' }}
                                </span>
                            </td>
                        </template>
                        <template #product_id="{item}">
                            <td>{{ item.product_id ?? '' }}</td>
                        </template>
                        <template #actions="{item}">
                            <td>
                                <DButton
                                    v-if="showUpdateAccountingProductButton(item)"
                                    class="pointer-events float-right"
                                    icon="pencil"
                                    mini
                                    @click="showUpdateAccountingProductModalForItem(item)"
                                />
                            </td>
                        </template>
                    </DTable>
                </DCol>
            </DRow>
        </DCard>
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
        </DFabs>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <DModal
            v-model="activityLogComment"
            v-model:show="showCancelModal"
            :title="cancellationTitle"
            :message="cancellationMessage"
            is-confirm
            :is-awaiting-validation="true"
            :show-change-reason-input="true"
            is-error
            :show-incident-list="true"
            :customer-id="customer?.id ?? 0"
            @setIncident="setIncident"
            @close="showCancelModal = false"
            @confirm="cancel"
        />

        <EditInstallmentsModal
            v-if="showInstallmentsEditModal"
            :show-modal="showInstallmentsEditModal"
            :invoice="invoice"
            @update:showModal="showInstallmentsEditModal = $event"
        />
        <ChangePaymentMethod
            v-if="showChangePaymentMethodModal"
            :show-modal="showChangePaymentMethodModal"
            :invoice="invoice"
            :payment-methods="paymentMethods"
            @close-modal="showChangePaymentMethodModal = false"
        />

        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
        <UpdateAccountingProductModal
            v-if="showUpdateAccountingProductModal"
            v-model:showModal="showUpdateAccountingProductModal"
            :item="updateAccountingProductItem"
            :accounting-product-options="accountingProducts"
            :customer-id="customer.id"
            :invoice-id="invoice.id"
            :errors="errors"
        />
        <UpdateAccountingProductGroupModal
            v-if="showUpdateAccountingProductGroupModal"
            v-model:showModal="showUpdateAccountingProductGroupModal"
            :item="updateAccountingProductItem"
            :accounting-product-options="accountingProducts"
            :customer-id="customer.id"
            :invoice-id="invoice.id"
            :errors="errors"
            :group-name="groupNameItem"
        />
        <SetDebtLossModal
            v-if="showSetDebtLossModal"
            :show-modal="showSetDebtLossModal"
            :invoice="invoice"
            @close-modal="closeSetDebtLossModal()"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import priceFormat from '~@/Mixins/priceFormat'
import _ from 'lodash'
import EditInstallmentsModal from '~@/Components/Customers/Invoices/EditInstallmentsModal.vue'
import ChangePaymentMethod from '~@/Components/Customers/Invoices/ChangePaymentMethod.vue'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import UpdateAccountingProductModal from '~@/Components/Customers/Invoices/UpdateAccountingProductModal.vue'
import UpdateAccountingProductGroupModal from '~@/Components/Customers/Invoices/UpdateAccountingProductGroupModal'
import SetDebtLossModal from '~@/Components/Customers/Invoices/SetDebtLossModal.vue'

export default {
    name: 'CustomerInvoicesShow',
    components: {
        SetDebtLossModal,
        UpdateAccountingProductModal,
        UpdateAccountingProductGroupModal,
        ChangePaymentMethod,
        EditInstallmentsModal,
        CreateCloudPitCustomerModal,
        SSOFab,
        IncidentFlag,
        ListViewModal
    },
    mixins: [priceFormat],
    layout: [App, CustomerBase],
    props: {
        invoice: {
            type: Object,
            default () {
                return {}
            }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        },
        paymentMethods: {
            type: Object,
            default: () => { return {} }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        accountingProducts: {
            type: Object,
            default: () => ({})
        },
        errors: {
            type: Object,
            default: () => ({})
        },
        netAmountsSum: {
            type: Number,
            default: () => (0)
        },
        discountsSum: {
            type: Number,
            default: () => (0)
        }
    },
    data () {
        return {
            activityLogComment: '',
            editInstallments: '',
            groupedItemFields: [
                { key: 'group_name', label: 'Group name' },
                { key: 'tax_rate', label: this.__('customers.invoices.tax_rate') },
                { key: 'from_date', label: this.__('customers.invoices.form.from_date') },
                { key: 'to_date', label: this.__('customers.invoices.form.to_date') },
                { key: 'price', label: this.__('customers.invoices.form.price') },
                { key: 'quantity', label: this.__('customers.invoices.form.quantity') },
                { key: 'net_amount', label: this.__('customers.invoices.net_amount') },
                { key: 'action', label: '' }
            ],
            installments: [
                { key: 'number', label: this.__('customers.invoices.installments.number') },
                { key: 'due_date', label: this.__('customers.invoices.installments.due_date') },
                { key: 'amount', label: this.__('customers.invoices.installments.amount') },
                { key: 'open_items', label: this.__('customers.invoices.installments.open_items') },
                { key: 'amount_to_pay', label: this.__('customers.invoices.installments.amount_to_pay') },
                { key: 'status', label: this.__('customers.invoices.installments.status') },
                { key: 'dunning_status', label: this.__('customers.invoices.dunning_status') },
                ...(this.checkPaymentMethodIsSepa(this.invoice)
                    ? [
                        { key: 'sepa_transaction', label: this.__('customers.invoices.sepa_transaction') },
                        { key: 'sepa_booking_date', label: this.__('customers.invoices.sepa_booking_date') }
                    ]
                    : [])
            ],
            filterData: _.cloneDeep(this.filters),
            showCancelModal: false,
            showCreateCloudpitCustomerModal: false,
            showInstallmentsEditModal: false,
            incident_id: null,
            showChangePaymentMethodModal: false,
            showListViewModal: false,
            showUpdateAccountingProductModal: false,
            showUpdateAccountingProductGroupModal: false,
            updateAccountingProductItem: {},
            groupNameItem: '',
            showSetDebtLossModal: false
        }
    },
    computed: {
        cancelAvailable () {
            if (this.invoice.type.startsWith('invoice')) {
                return this.userPermissions.includes('invoice.cancel') &&
                    this.invoice.type !== 'invoice_cancellation' &&
                    this.invoice.active_item_exists
            }

            return this.userPermissions.includes('credit_note.manage') &&
                this.invoice.type !== 'credit_note_cancellation' &&
                this.invoice.active_item_exists
        },
        cancellationMessage () {
            if (this.invoice.type.startsWith('invoice')) {
                return this.__('customers.invoices.confirm_title')
            }

            return this.__('customers.credit_notes.confirm_message')
        },
        cancellationTitle () {
            if (this.invoice.type.startsWith('invoice')) {
                return this.__('customers.invoices.confirm_title')
            }

            return this.__('customers.credit_notes.confirm_message')
        },
        customerData () {
            return (this.invoice.company ? this.invoice.company + ', ' : '') +
                (this.invoice.title ? this.invoice.title + ' ' : '') +
                (this.invoice.firstname ? this.invoice.firstname + ' ' : '') +
                (this.invoice.lastname ? this.invoice.lastname + ', ' : '') +
                (this.invoice.address1 ? this.invoice.address1 + ', ' : '') +
                (this.invoice.address2 ? this.invoice.address2 + ', ' : '') +
                (this.invoice.postal_code ? this.invoice.postal_code + ', ' : '') +
                (this.invoice.country ? this.invoice.country : '')
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        paymentCondition () {
            let text = ''

            if (this.invoice.paymentTerm !== null) {
                text += ', ' + this.invoice.paymentTerm + ' ' + this.__('customers.invoices.days')
            }

            if (this.invoice.arePromptCashDiscountEnabled === true &&
                this.invoice.promptCashDiscountPercentage !== null &&
                this.invoice.promptCashDiscountPercentage > 0
            ) {
                text += ', ' +
                    this.invoice.promptCashDiscountDays + ' ' +
                    this.__('customers.invoices.days') + ' ' +
                    this.invoice.promptCashDiscountPercentage +
                    '%'
            }

            if (this.invoiceWithInstallments(this.invoice)) {
                text += ', ' + this.invoice.installments.length + ' ' + this.__('customers.invoices.installments.installments')
            }

            return text
        },
        invoiceDecimals () {
            return this.$page.props.marketRegions[this.customer.market_region_id]?.decimal_places ?? 2
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' && item.column !== 'icon' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        }
    },
    methods: {
        roundPrice (value) {
            if (value || value === 0.0) {
                return this.priceFormat(value, this.invoiceDecimals, this.customer.market_region_id)
            }

            return ''
        },
        showQuantity (value) {
            if (value) {
                return value
            }

            return ''
        },
        taxRate (value) {
            if (value) {
                return this.pricePercentage(value, 2)
            }

            return ''
        },
        getDownloadInvoiceLink () {
            if (this.invoice.type.startsWith('invoice')) {
                return this.$route('tools.invoices.download', {
                    customer: this.invoice.customer.id,
                    invoice: this.invoice.id
                })
            }

            return this.$route('customers.customers.credit-notes.download', {
                customer: this.invoice.customer.id,
                credit_note: this.invoice.id
            })
        },
        getDownloadDunningLink (dunningId) {
            return this.$route('tools.dunning.download', { dunning: dunningId })
        },
        getDownloadInvoiceCsvLink (invoice) {
            return this.$route('tools.invoices.download-csv', {
                customer: this.customer.id,
                invoice: invoice.id
            })
        },
        getDownloadInvoicePartnersSubContractsCsvLink (invoice) {
            return this.$route('tools.invoices.download-partner-subcontracts-csv', {
                customer: this.customer.id,
                invoice: invoice.id
            })
        },
        filter (filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            this.invoice.type.startsWith('invoice')
                ? this.$inertia.get(this.$route('customers.customers.invoices.show', {
                    customer: this.customer.id,
                    invoice: this.invoice.id
                }), queryParameters)
                : this.$inertia.get(this.$route('customers.customers.credit-notes.show', {
                    customer: this.customer.id,
                    credit_note: this.invoice.id
                }), queryParameters)
        },
        resetFilters () {
            this.invoice.type.startsWith('invoice')
                ? this.$inertia.get(this.$route('customers.customers.invoices.show', {
                    customer: this.customer.id,
                    invoice: this.invoice.id
                }), {})
                : this.$inertia.get(this.$route('customers.customers.credit-notes.show', {
                    customer: this.customer.id,
                    credit_note: this.invoice.id
                }), {})
        },
        resendInvoiceEmail (invoice) {
            return this.$route('tools.invoices.resend', {
                invoice: invoice.id
            })
        },
        triggerPayment (invoice) {
            return this.$inertia.post(this.$route('tools.invoices.trigger-payment', {
                invoice: invoice.id
            }), {})
        },
        groupItemsList (filters, item) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))

            queryParameters.items_group = item.items_group
            queryParameters.product_group = item.product_group
            queryParameters.external_product_id = item.external_product_id
            queryParameters.from_date = item.from_date
            queryParameters.to_date = item.to_date
            queryParameters.first_item_price = item.first_item_price
            queryParameters.contract_id = item.contract_id

            this.invoice.type.startsWith('invoice')
                ? this.$inertia.get(this.$route('customers.customers.invoices.items-list',
                    {
                        customer: this.customer.id,
                        invoice: this.invoice.id
                    }
                ), queryParameters)
                : this.$inertia.get(this.$route('customers.customers.credit-notes.items-list',
                    {
                        customer: this.customer.id,
                        credit_note: this.invoice.id
                    }
                ), queryParameters)
        },
        ccPaymentsLink () {
            const filter = {
                reference: null,
                transactionStatus: null,
                invoiceId: this.invoice.id,
                customerId: null
            }

            return this.$route('tools.credit-card.transactions', { _query: filter })
        },
        cancel () {
            if (this.invoice.type.startsWith('invoice')) {
                this.$inertia.post(this.$route('customers.customers.invoices.cancel', {
                    customer: this.customer.id,
                    invoice: this.invoice.id
                }), {
                    activity_log_comment: this.activity_log_comment,
                    incident_id: this.incident_id
                }, {
                    onSuccess: () => {
                        this.showCancelModal = false
                    }
                })
            } else {
                this.$inertia.post(this.$route('customers.customers.credit-notes.cancel', {
                    customer: this.customer.id,
                    credit_note: this.invoice.id
                }), {
                    activity_log_comment: this.activity_log_comment,
                    incident_id: this.incident_id
                }, {
                    onSuccess: () => {
                        this.showCancelModal = false
                    }
                })
            }
        },
        cancelPartially () {
            this.$inertia.get(this.$route(
                'customers.customers.invoices.items',
                { customer: this.customer.id, invoice: this.invoice.id }
            ))
        },
        editableInstallment (installment) {
            return installment.status === 'unpaid' &&
                (installment.open_items !== null
                    ? installment.amount === installment.open_items
                    : installment.amount === installment.amount_to_pay
                )
        },
        editInstallmentsAvailable () {
            return this.userPermissions.includes('invoice.edit_installmentplan') &&
                this.invoice.installments.some(installment => this.editableInstallment(installment))
        },
        setIncident (value) {
            this.incident_id = value
        },
        formatInvoiceStatus (invoice) {
            const statusText = this.__('customers.contacts.invoice_status.' + invoice.status)
            if (invoice.prepaid) {
                return statusText + ' (' + this.__('customers.contacts.invoice_status.prepaid') + ')'
            }

            return statusText
        },
        calculateTaxAmount (item) {
            // Not calculate discount added data items
            if (item.id === '') {
                return ''
            }

            // If tax exemption and no tax on invoice item/credit note
            if (item.no_tax_on_invoice || item.no_tax) {
                return '0.00'
            }

            return this.roundPrice((item.unrounded_total_net_amount * (item.tax_rate / 100)).toFixed(parseInt(this.invoiceDecimals)))
        },
        showUpdateAccountingProductModalForItem (item) {
            if (this.invoice.shouldGroupItems) {
                this.groupNameItem = item.group_name
                this.updateAccountingProductItem = item
                this.showUpdateAccountingProductGroupModal = true
            } else {
                this.updateAccountingProductItem = item
                this.showUpdateAccountingProductModal = true
            }
        },
        showUpdateAccountingProductButton (item) {
            return item.has_default_accounting_product &&
                this.invoice.finance_system_synced_at === null
        },
        invoiceWithInstallments (invoice) {
            return invoice?.installments && this.invoice.installments.length > 0
        },
        checkPaymentMethodIsSepa (invoice) {
            return ['sepa', 'sepa-btb', 'ideal'].includes(invoice.payment_method)
        },
        hasAlternativePaymentMethods (invoice) {
            return this.paymentMethods.some((paymentMethod) => paymentMethod.value !== invoice.payment_method)
        },
        sepaResetAvailable (invoice) {
            return this.userPermissions.includes('invoice.reset_unhandled_sepa_payments') &&
                this.checkPaymentMethodIsSepa(invoice) &&
                invoice.type !== 'invoice_cancellation' &&
                invoice.prepaid === false
        },
        sepaResetAvailableInstallments (invoice) {
            return this.sepaResetAvailable(invoice) &&
                invoice?.installments?.some(installment =>
                    installment.status === 'payment_pending' &&
                    installment.sepa_transaction === null
                )
        },
        sepaResetAvailableInvoice (invoice) {
            return this.sepaResetAvailable(invoice) && invoice.sepa_transaction === null
        },
        sepaReset (item) {
            return this.$inertia.post(this.$route('customers.customers.invoices.reset-unhandled-sepa-payments', {
                customer: this.customer,
                invoice: item.id
            }), {})
        },
        openSetDebtLossModal () {
            this.showSetDebtLossModal = true
        },
        closeSetDebtLossModal () {
            this.showSetDebtLossModal = false
        }
    }
}
</script>

<style lang="scss" scoped>
.action-list > * {
    margin-right: 5px;
}
.d-form-row {
    margin-bottom: 5px!important;
}
.instalments-container {
    max-width: 700px;
}
</style>
